import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { submitLead, sendToWeb3Forms } from "@/lib/api";

const SERVICE_OPTIONS = [
  "Implementation",
  "Migrations",
  "Application Development",
  "Integration",
  "Rollouts",
  "Upgrades",
  "Support & Maintenance",
  "Testing",
  "Careers — Application",
  "Other",
];

export default function Contact() {
  const location = useLocation();
  const prefill = location.state || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: prefill.prefillService || "",
    message: prefill.prefillMessage || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // When arriving from Careers with prefill state, scroll form into view and gently notify.
  useEffect(() => {
    if (prefill.prefillMessage) {
      toast.info("We've started an application for you — just add your details.");
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="contact-form-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Please enter a valid email.";
    if (!form.message.trim() || form.message.trim().length < 5) errs.message = "Please share a brief message.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        company: form.company || null,
        phone: form.phone || null,
        service_interest: form.service_interest || null,
        source: "website-contact",
      };
      // Fire both in parallel: DB persistence + Web3Forms email notification
      const [, emailRes] = await Promise.all([
        submitLead(payload),
        sendToWeb3Forms(payload).catch((err) => ({ success: false, message: String(err) })),
      ]);
      if (emailRes && emailRes.success === false && !emailRes.skipped) {
        // eslint-disable-next-line no-console
        console.warn("Web3Forms notification failed:", emailRes.message);
      }
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="overline">Contact</div>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05] mt-6 text-[#0F172A]">
              Tell us about your SAP <br/>ambition.
            </h1>
            <p className="text-lg text-gray-600 mt-8 max-w-xl leading-relaxed">
              A senior consultant will reply within one business day. No call-centres, no junior gatekeeping.
            </p>
          </div>
          <div className="md:col-span-5 md:pl-12 md:border-l md:border-gray-200">
            <div className="overline mb-6">Direct lines</div>
            <ul className="space-y-6 text-[15px] text-gray-700">
              <li className="flex items-start gap-3">
                <Mail size={18} className="mt-0.5 text-[#111827] shrink-0" />
                <span>info@sunsrisoft.com</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="mt-0.5 text-[#111827] shrink-0" />
                <span className="leading-relaxed">
                  <span className="block"><span className="text-gray-500 mr-1">IN</span> +91 87900 06313</span>
                  <span className="block"><span className="text-gray-500 mr-1">USA</span> +1 (510) 991-6480</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[#111827] shrink-0" />
                <span className="leading-relaxed">
                  <span className="block overline text-gray-500 mb-1">India</span>
                  <span className="block">301 &amp; 302, Surya Pearl Building,<br/>Opp: Raheja Mindspace IT Park,<br/>Hitech City, Hyderabad – 500081</span>
                  <span className="block overline text-gray-500 mt-4 mb-1">USA</span>
                  <span className="block">7645 Donohue Dr,<br/>Dublin CA 94568</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {submitted ? (
            <Reveal>
              <div data-testid="contact-success" className="bg-white border border-gray-200 p-12 text-center">
                <CheckCircle2 size={40} className="text-[#111827] mx-auto" strokeWidth={1.5} />
                <h2 className="font-serif text-3xl sm:text-4xl mt-6 text-[#0F172A]">Your message is in.</h2>
                <p className="text-gray-600 mt-4 max-w-md mx-auto leading-relaxed">
                  Thank you for reaching out. A senior member of our SAP team will respond within one business day.
                </p>
                <button data-testid="contact-send-another" className="btn-outline mt-10" onClick={() => setSubmitted(false)}>
                  Send another message
                </button>
              </div>
            </Reveal>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="bg-white border border-gray-200 p-8 sm:p-12 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline block mb-2" htmlFor="name">Full name *</label>
                  <input id="name" data-testid="input-name" className="editorial-input" value={form.name} onChange={update("name")} placeholder="Jane Doe" required />
                  {errors.name && <div className="text-xs text-red-600 mt-1" data-testid="error-name">{errors.name}</div>}
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="email">Email *</label>
                  <input id="email" type="email" data-testid="input-email" className="editorial-input" value={form.email} onChange={update("email")} placeholder="jane@company.com" required />
                  {errors.email && <div className="text-xs text-red-600 mt-1" data-testid="error-email">{errors.email}</div>}
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="company">Company</label>
                  <input id="company" data-testid="input-company" className="editorial-input" value={form.company} onChange={update("company")} placeholder="ACME Industries" />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="phone">Phone</label>
                  <input id="phone" data-testid="input-phone" className="editorial-input" value={form.phone} onChange={update("phone")} placeholder="+91 98xxxxxxxx" />
                </div>
              </div>

              <div>
                <label className="overline block mb-2" htmlFor="service_interest">Service interest</label>
                <select id="service_interest" data-testid="input-service" className="editorial-input" value={form.service_interest} onChange={update("service_interest")}>
                  <option value="">Select a service…</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="overline block mb-2" htmlFor="message">Message *</label>
                <textarea id="message" data-testid="input-message" rows={6} className="editorial-input resize-y" value={form.message} onChange={update("message")} placeholder="Tell us about your SAP landscape, timelines, and what you'd like to achieve." required />
                {errors.message && <div className="text-xs text-red-600 mt-1" data-testid="error-message">{errors.message}</div>}
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-gray-500 max-w-xs">By submitting, you consent to Sunsri Software contacting you regarding your enquiry.</p>
                <button type="submit" data-testid="contact-submit-button" className="btn-primary" disabled={submitting}>
                  {submitting ? "Sending…" : "Send enquiry"} <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
